import { useState, useEffect, useCallback } from 'react';
import { setlistsApi } from '@/api/setlists';
import { apiErrorMessage } from '@/utils/apiError';
import SetlistMediaLinks from '@/components/feature/SetlistMediaLinks';
import type { SetlistDetail as SetlistDetailType, SetlistItem } from '@/types/setlist';
import AddSongToSetlistModal from './AddSongToSetlistModal';
import SetlistSongViewer from './SetlistSongViewer';
import { formatSongKey } from '@/utils/songKey';

interface Props {
  teamId: string;
  ministryId: string;
  setlistId: string;
  canWrite: boolean;
  onBack: () => void;
  onDeleted: () => void;
}

export default function SetlistDetail({ teamId, ministryId, setlistId, canWrite, onBack, onDeleted }: Props) {
  const [setlist, setSetlist] = useState<SetlistDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [editingKeyOverrideId, setEditingKeyOverrideId] = useState<string | null>(null);
  const [keyOverrideValue, setKeyOverrideValue] = useState('');
  const [editingLinksId, setEditingLinksId] = useState<string | null>(null);
  const [youtubeUrlValue, setYoutubeUrlValue] = useState('');
  const [spotifyUrlValue, setSpotifyUrlValue] = useState('');
  const [addingSong, setAddingSong] = useState(false);
  const [viewingSong, setViewingSong] = useState<SetlistItem | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await setlistsApi.get(teamId, setlistId);
      setSetlist(res.data);
      setNameValue(res.data.name);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [teamId, setlistId]);

  useEffect(() => { load(); }, [load]);

  const handleRename = async () => {
    if (!nameValue.trim() || !setlist) return;
    try {
      const res = await setlistsApi.update(teamId, setlistId, { name: nameValue.trim() });
      setSetlist(res.data);
      setEditingName(false);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  const handleDelete = async () => {
    if (!confirm('Excluir este setlist?')) return;
    try {
      await setlistsApi.delete(teamId, setlistId);
      onDeleted();
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  const handleRemoveItem = async (item: SetlistItem) => {
    try {
      await setlistsApi.removeItem(teamId, setlistId, item.id);
      setSetlist((prev) => prev ? { ...prev, items: prev.items.filter((i) => i.id !== item.id) } : prev);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  const handleStartKeyOverride = (item: SetlistItem) => {
    setEditingKeyOverrideId(item.id);
    setKeyOverrideValue(item.key_override || '');
  };

  const handleSaveKeyOverride = async (item: SetlistItem) => {
    const val = keyOverrideValue.trim();
    setEditingKeyOverrideId(null);
    if (val === (item.key_override || '')) return;
    try {
      const res = await setlistsApi.updateItem(teamId, setlistId, item.id, { key_override: val });
      setSetlist((prev) => prev ? {
        ...prev,
        items: prev.items.map((i) => i.id === item.id ? res.data : i),
      } : prev);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  const handleStartLinkEditing = (item: SetlistItem) => {
    setEditingLinksId(item.id);
    setYoutubeUrlValue(item.youtube_url);
    setSpotifyUrlValue(item.spotify_url);
  };

  const handleSaveLinks = async (item: SetlistItem) => {
    try {
      const res = await setlistsApi.updateItem(teamId, setlistId, item.id, {
        youtube_url: youtubeUrlValue.trim(),
        spotify_url: spotifyUrlValue.trim(),
      });
      setSetlist((prev) => prev ? {
        ...prev,
        items: prev.items.map((current) => current.id === item.id ? res.data : current),
      } : prev);
      setEditingLinksId(null);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  const handleDragStart = (id: string) => setDraggingId(id);

  const handleDrop = async (targetIdx: number) => {
    if (!setlist || draggingId === null) return;
    const items = [...setlist.items];
    const fromIdx = items.findIndex((i) => i.id === draggingId);
    if (fromIdx === -1 || fromIdx === targetIdx) { setDraggingId(null); setOverIdx(null); return; }
    const [moved] = items.splice(fromIdx, 1);
    items.splice(targetIdx, 0, moved);
    const reordered = items.map((item, idx) => ({ ...item, order: idx }));
    setSetlist((prev) => prev ? { ...prev, items: reordered } : prev);
    setDraggingId(null);
    setOverIdx(null);
    try {
      const res = await setlistsApi.reorderItems(teamId, setlistId, { item_ids: reordered.map((i) => i.id) });
      setSetlist(res.data);
    } catch (e) {
      setError(apiErrorMessage(e));
      load();
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="w-5 h-5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin"></div>
    </div>
  );

  if (!setlist) return (
    <div className="flex items-center justify-center h-full">
      <p className="text-red-400 text-sm">{error || 'Setlist não encontrado.'}</p>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/5 flex items-center gap-3">
        <button onClick={onBack} className="text-white/40 hover:text-white/70 transition-colors">
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <div className="flex-1 min-w-0">
          {editingName ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditingName(false); }}
                className="flex-1 bg-white/5 border border-white/20 rounded-lg px-3 py-1 text-white text-base outline-none focus:border-[#C8A96E]/50"
              />
              <button onClick={handleRename} className="text-[#C8A96E] hover:text-[#D4B87A] text-sm font-medium">Salvar</button>
              <button onClick={() => setEditingName(false)} className="text-white/40 hover:text-white/70 text-sm">Cancelar</button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h2 className="text-white font-semibold text-base truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                {setlist.name}
              </h2>
              {canWrite && (
                <button onClick={() => setEditingName(true)} className="text-white/30 hover:text-white/60 transition-colors">
                  <i className="ri-edit-2-line text-sm"></i>
                </button>
              )}
            </div>
          )}
          <p className="text-white/30 text-xs mt-0.5">{setlist.items.length} músicas</p>
        </div>
        {canWrite && (
          <button
            type="button"
            aria-label="Adicionar música"
            onClick={() => setAddingSong(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#C8A96E]/15 text-[#C8A96E] hover:bg-[#C8A96E]/25 text-xs font-medium transition-colors shrink-0"
          >
            <i className="ri-add-line text-sm" />
            <span>Adicionar música</span>
          </button>
        )}
        {canWrite && (
          <button onClick={handleDelete} className="text-red-400/60 hover:text-red-400 transition-colors ml-2">
            <i className="ri-delete-bin-2-line text-base"></i>
          </button>
        )}
      </div>

      {error && <p className="text-red-400 text-xs px-6 py-2">{error}</p>}

      {/* Items */}
      <div className="flex-1 overflow-y-auto">
        {setlist.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <i className="ri-music-2-line text-white/20 text-3xl mb-2"></i>
            <p className="text-white/30 text-sm">Nenhuma música neste setlist.</p>
            <p className="text-white/20 text-xs mt-1">Use o botão "Adicionar ao setlist" na biblioteca.</p>
          </div>
        ) : (
          setlist.items.map((item, idx) => (
            <div
              key={item.id}
              draggable={canWrite}
              onDragStart={() => handleDragStart(item.id)}
              onDragOver={(e) => { e.preventDefault(); setOverIdx(idx); }}
              onDrop={() => handleDrop(idx)}
              onDragEnd={() => { setDraggingId(null); setOverIdx(null); }}
              className={`flex items-center gap-3 px-6 py-3 border-b border-white/5 transition-colors ${
                draggingId === item.id ? 'opacity-40' : ''
              } ${overIdx === idx && draggingId !== item.id ? 'bg-white/10' : 'hover:bg-white/5'}`}
            >
              <span className="text-white/20 text-xs w-5 text-right shrink-0">{idx + 1}</span>
              {canWrite && <i className="ri-drag-move-2-line text-white/20 cursor-grab text-sm shrink-0"></i>}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{item.song_title}</p>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <p className="text-white/40 text-xs truncate">{item.song_artist || 'Sem artista'}{item.song_key ? ` · ${formatSongKey(item.song_key, item.song_mode)}` : ''}</p>
                  {/* key_override display / editor */}
                  {editingKeyOverrideId === item.id ? (
                    <input
                      autoFocus
                      value={keyOverrideValue}
                      onChange={(e) => setKeyOverrideValue(e.target.value.toUpperCase().slice(0, 5))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveKeyOverride(item);
                        if (e.key === 'Escape') setEditingKeyOverrideId(null);
                      }}
                      onBlur={() => handleSaveKeyOverride(item)}
                      placeholder="Tom (ex: D#)"
                      className="w-20 bg-white/10 border border-[#C8A96E]/40 rounded px-1.5 py-0 text-[#C8A96E] text-xs outline-none focus:border-[#C8A96E]"
                    />
                  ) : (
                    canWrite && (
                      <button
                        onClick={() => handleStartKeyOverride(item)}
                        title="Definir tom específico para este evento"
                        className={`text-xs px-1.5 py-0 rounded border transition-colors ${
                          item.key_override
                            ? 'border-[#C8A96E]/60 text-[#C8A96E] hover:border-[#C8A96E]'
                            : 'border-white/10 text-white/20 hover:text-white/40 hover:border-white/20'
                        }`}
                      >
                        {item.key_override ? `Tom: ${item.key_override}` : <i className="ri-music-2-line"></i>}
                      </button>
                    )
                  )}
                  {!canWrite && item.key_override && (
                    <span className="text-[#C8A96E] text-xs border border-[#C8A96E]/40 rounded px-1.5">Tom: {item.key_override}</span>
                  )}
                  <button
                    type="button"
                    aria-label={`Cifra de ${item.song_title}`}
                    onClick={() => setViewingSong(item)}
                    className="inline-flex min-h-8 items-center gap-1.5 rounded-md border border-[#E8724F]/30 bg-[#E8724F]/10 px-2.5 text-xs font-medium text-[#F19A7F] transition-colors hover:border-[#E8724F]/60 hover:bg-[#E8724F]/20"
                  >
                    <i className="ri-eye-line" aria-hidden="true" />
                    Cifra
                  </button>
                  <SetlistMediaLinks
                    songTitle={item.song_title}
                    youtubeUrl={item.youtube_url}
                    spotifyUrl={item.spotify_url}
                  />
                  {canWrite && (
                    <button
                      type="button"
                      aria-label={`Editar links de ${item.song_title}`}
                      onClick={() => handleStartLinkEditing(item)}
                      className="inline-flex min-h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-white/10 px-2.5 text-xs text-white/55 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      <i className="ri-links-line"></i>
                      <span>Editar links</span>
                    </button>
                  )}
                </div>
                {editingLinksId === item.id && (
                  <div className="mt-3 grid gap-2 rounded-lg border border-white/10 bg-black/20 p-3">
                    <label className="text-white/40 text-xs">
                      YouTube
                      <input
                        type="url"
                        value={youtubeUrlValue}
                        onChange={(e) => setYoutubeUrlValue(e.target.value)}
                        className="mt-1 w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-[#C8A96E]/50"
                      />
                    </label>
                    <label className="text-white/40 text-xs">
                      Spotify
                      <input
                        type="url"
                        value={spotifyUrlValue}
                        onChange={(e) => setSpotifyUrlValue(e.target.value)}
                        className="mt-1 w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-[#C8A96E]/50"
                      />
                    </label>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingLinksId(null)}
                        className="px-2 py-1 text-white/40 text-xs hover:text-white/70"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveLinks(item)}
                        className="px-2 py-1 rounded bg-[#C8A96E] text-[#0A0A0F] text-xs font-medium"
                      >
                        Salvar links
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {canWrite && (
                <button
                  onClick={() => handleRemoveItem(item)}
                  className="text-white/20 hover:text-red-400 transition-colors ml-1"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {addingSong && (
        <AddSongToSetlistModal
          teamId={teamId}
          ministryId={ministryId}
          setlistId={setlistId}
          existingSongIds={setlist.items.map((item) => item.song_id)}
          onClose={() => setAddingSong(false)}
          onAdded={load}
        />
      )}
      {viewingSong && (
        <SetlistSongViewer
          key={viewingSong.id}
          ministryId={ministryId}
          item={viewingSong}
          onClose={() => setViewingSong(null)}
        />
      )}
    </div>
  );
}
